import { HStack, Icon, Flex, VStack, Button, Text, Center, Box } from '@cad-arc';
import classnames from 'classnames';
import { format, isSameDay, isSameYear, parseJSON } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import { AdditionalInfoType, AdditionalInfoView, Priority } from '../../types';
import { useAdditionalInfos } from '../../hooks/use-additional-infos/use-additional-infos';
import styles from './additional-info-row.module.css';
import { FlagRenderer } from '../../../common/components/flag-renderer/flag-renderer';
import { useShowErrorAlert } from '../../hooks/use-show-error-alert/use-show-error-alert';
import { colors } from '../../../colors/colors';

export const formatAdditionalInfoTime = (date: Date, now: Date): string => {
    // \xa0 = Non-breaking space
    if (isSameDay(date, now)) return format(date, 'HH:mm:ss');
    if (isSameYear(date, now)) return format(date, 'LL/dd\xa0HH:mm');
    return format(date, 'LL/dd/yyyy\xa0HH:mm');
};

const formatAdditionalInfoAuthor = (info: AdditionalInfoView): string => {
    return info.authorCallSign
        ? info.authorCallSign
        : info.userCadId
        ? `${info.userInitials}-\n${info.userCadId}`
        : info.userInitials;
};

const isProQaInitOrEndMessage = (info: AdditionalInfoView): boolean => {
    return (
        info.type === AdditionalInfoType.PROQA &&
        info.text.indexOf('<') >= 0 &&
        info.text.indexOf('>') >= 0
    );
};

const getInfoTextTestId = (info: AdditionalInfoView): string => {
    switch (info.type) {
        case AdditionalInfoType.USER:
            return 'ADDITIONAL_NARRATIVE';
        case AdditionalInfoType.SYSTEM:
            return 'ADDITIONAL_NARRATIVE_SYSTEM';
        case AdditionalInfoType.PROQA:
            return 'ADDITIONAL_NARRATIVE_PROQA';
        case AdditionalInfoType.PRO_QA_DESC_INFO:
            return 'ADDITIONAL_NARRATIVE_PROQA_DESC_INFO';
        case AdditionalInfoType.PRO_QA_COMMENT:
            return 'ADDITIONAL_NARRATIVE_PROQA_COMMENT';
        case AdditionalInfoType.PRO_QA_KEY_QUESTION:
            return 'ADDITIONAL_NARRATIVE_PROQA_KEY_Q';
        case AdditionalInfoType.PRO_QA_CRITICAL:
            return 'ADDITIONAL_NARRATIVE_PROQA_CRITICAL';
        case AdditionalInfoType.PRO_QA_SCRIPT:
            return 'ADDITIONAL_NARRATIVE_PROQA_SCRIPT';
        case AdditionalInfoType.PRO_QA_UPDATE:
            return 'ADDITIONAL_NARRATIVE_PROQA_UPDATE';
        case AdditionalInfoType.PRO_QA_URGENT:
            return 'ADDITIONAL_NARRATIVE_PROQA_URGENT';
        default:
            return '';
    }
};

export const PRO_QA_TYPES = [
    AdditionalInfoType.PROQA,
    AdditionalInfoType.PRO_QA_DESC_INFO,
    AdditionalInfoType.PRO_QA_KEY_QUESTION,
    AdditionalInfoType.PRO_QA_COMMENT,
    AdditionalInfoType.PRO_QA_CRITICAL,
    AdditionalInfoType.PRO_QA_SCRIPT,
    AdditionalInfoType.PRO_QA_UPDATE,
    AdditionalInfoType.PRO_QA_URGENT,
];

const UnreadLine = ({ isUrgent }: { isUrgent: boolean }) => {
    const color = isUrgent ? 'var(--arc-colors-urgent-default)' : colors.messagesUnreadLineNormal;
    return (
        <Center width="100%" height="var(--arc-space-4)">
            <Flex flexDirection="row" width="100%">
                <Center flexGrow={1}>
                    <Flex flexGrow={1} borderTop={`1px solid ${color}`} />
                </Center>
                <Box paddingY="var(--arc-space-1)">
                    <Text variant="headingXs" style={{ color }}>
                        {isUrgent ? 'Urgent' : 'New'}
                    </Text>
                </Box>
            </Flex>
        </Center>
    );
};

export const AdditionalInfoRow = observer(
    ({
        data: info,
        index,
        agencyEventNumberToIdMap,
        currentUserId,
        canDeleteInfos = true,
        onOpenEvent,
        isReadOnlyMode = false,
        isFirstResponder = false,
    }: {
        data: AdditionalInfoView;
        index: number;
        agencyEventNumberToIdMap?: Record<string, number>;
        currentUserId?: number;
        canDeleteInfos?: boolean;
        onOpenEvent?: (id: number) => void;
        isReadOnlyMode?: boolean;
        isFirstResponder?: boolean;
    }) => {
        const { entityId, earliestUnreadInfo, deleteInfo, entityNumber } = useAdditionalInfos();
        const { showFailedToDeleteAlert } = useShowErrorAlert();

        const [isDeleting, setIsDeleting] = useState<boolean>(false);
        const handleDelete = useCallback(async () => {
            try {
                setIsDeleting(true);
                await deleteInfo(info.id);
            } catch {
                showFailedToDeleteAlert(entityNumber);
            } finally {
                setIsDeleting(false);
            }
        }, [entityNumber, deleteInfo, info.id, showFailedToDeleteAlert]);
        const isProQA = PRO_QA_TYPES.includes(info.type as AdditionalInfoType);
        const isProQAInitOrEndMsg = isProQaInitOrEndMessage(info);
        const hasTrashIcon =
            canDeleteInfos &&
            currentUserId &&
            currentUserId === info.authorUserId &&
            info.type === AdditionalInfoType.USER &&
            !info.isEventNarrative &&
            !isProQA;

        const isUnread =
            info.id !== undefined && // the scheduled event info doesn't have an id
            earliestUnreadInfo?.id === info.id;

        const isUrgent = info.priority === Priority.HIGH;
        const infoTypeSystem = info.type === AdditionalInfoType.SYSTEM;

        return (
            <VStack
                w="100%"
                style={isProQA ? { backgroundColor: 'var(--arc-colors-brand-accent)' } : undefined}
            >
                <HStack
                    w="100%"
                    justify="start"
                    spacing={0}
                    data-testid={
                        info.priority === Priority.HIGH
                            ? 'HIGH_PRIORITY_ADDITIONAL_NARRATIVE_ROW'
                            : 'ADDITIONAL_INFO_ROW'
                    }
                    data-index={index}
                    data-infoid={info?.id}
                    className={classnames({
                        [styles['row']]: !isReadOnlyMode,
                        [styles['high-priority']]: isUrgent && !isReadOnlyMode,
                        [styles['user-is-author']]: currentUserId === info.authorUserId,
                        [styles['read-only-row']]: isReadOnlyMode,
                    })}
                >
                    <Box
                        align="center"
                        className={classnames(
                            styles[isFirstResponder ? 'row-icon-lg' : 'row-icon-md'],
                            {
                                [styles['is-deleting']]: isDeleting,
                            }
                        )}
                    >
                        {info.priority === Priority.HIGH && (
                            <Icon
                                data-testid="HIGH_PRIORITY_ADDITIONAL_NARRATIVE_ROW_ICON"
                                className={styles['exclamation-point']}
                                icon="ExclamationPoint"
                            />
                        )}
                        {isProQAInitOrEndMsg && <Icon icon="ProQa" className={styles['pro-qa']} />}
                        {hasTrashIcon && (
                            <Button
                                className={styles['trash']}
                                onClick={handleDelete}
                                isLoading={isDeleting}
                                data-testid="DELETE_ADDITIONAL_NARRATIVE_ICON"
                            >
                                <Icon icon="TrashCan" />
                            </Button>
                        )}
                    </Box>
                    <HStack justify="start" className={classnames(styles['row-text'])}>
                        <Text
                            data-testid={getInfoTextTestId(info)}
                            data-index={index}
                            as="div"
                            className={classnames(
                                styles['content'],
                                styles[isFirstResponder ? 'content-lg' : 'content-md'],
                                infoTypeSystem ? styles['system'] : '',
                                isFirstResponder && infoTypeSystem && styles['system-md'],
                                !isFirstResponder && infoTypeSystem && styles['system-sm'],
                                isProQAInitOrEndMsg ? styles['proqa-init-close'] : ''
                            )}
                        >
                            <FlagRenderer
                                text={info.text}
                                agencyEventNumberToIdMap={agencyEventNumberToIdMap}
                                isFirstResponder={isFirstResponder}
                                onAgencyEventNumberClick={({ eventId }) => {
                                    onOpenEvent?.(eventId);
                                }}
                            />
                        </Text>

                        <Text
                            as="div"
                            fontFamily="mono"
                            variant="caption"
                            color="text.secondary"
                            lineHeight="none"
                            className={classnames(
                                styles['date'],
                                isFirstResponder ? styles['date-md'] : styles['date-sm']
                            )}
                        >
                            {formatAdditionalInfoTime(parseJSON(info.createdDateUtc), new Date())}
                        </Text>
                        <Text
                            as="div"
                            fontFamily="mono"
                            variant="caption"
                            lineHeight="none"
                            color="text.secondary"
                            className={classnames(
                                styles['author'],
                                isFirstResponder ? styles['author-md'] : styles['author-sm']
                            )}
                        >
                            {formatAdditionalInfoAuthor(info)}
                            {info.parentAgencyEventId && info.parentAgencyEventId === entityId ? (
                                <div
                                    data-testid="PARENT_EVENT_LINK"
                                    className={styles['parent-event-link']}
                                    onClick={() => onOpenEvent?.(info.agencyEventId)}
                                >
                                    #{info.agencyEventNumber.slice(-4)}
                                    <Icon icon="OpenNewWindow" />
                                </div>
                            ) : (
                                // Align author column correctly for merged events with a dummy min-width element.
                                // No, putting the min-width on styles['author'] doesn't work.
                                <div
                                    className={styles['parent-event-link']}
                                    style={{ height: '0px' }}
                                />
                            )}
                        </Text>
                    </HStack>
                </HStack>
                {isUnread && <UnreadLine isUrgent={isUrgent} />}
            </VStack>
        );
    }
);
